import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  ViewChild,
  SimpleChanges,
  OnDestroy,
  Output,
  EventEmitter,
} from '@angular/core';
import {
  Intersection,
  LinearSRGBColorSpace,
  OrthographicCamera,
  PerspectiveCamera,
  Plane,
  Raycaster,
  Scene,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

import RenderingContext from '../../../../working-table/visualisation/rendering-context';
import { LoadingSpaceFactory } from '../../../../working-table/visualisation/loading-space-factory';
import { LoadingSpaceType } from '../../../../core/types';
import { Project } from '../../../../core/objects/project';
import {
  convertLoadingSpacesDimensions,
} from '../../../../shared/helpers/unitRecalculationHelpers';

@Component({
    selector: 'gl-loading-space-preview',
    templateUrl: './loading-space-preview.component.html',
    styleUrls: ['./loading-space-preview.component.scss'],
    standalone: false
})
export class LoadingSpacePreviewComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() isOrto = false;
  @Input() view = 'default';
  @Input() loadingSpaceType: LoadingSpaceType;
  @Input() width: number;
  @Input() height: number;
  @Input() project: Project;
  @Output() clicked = new EventEmitter<string[]>();
  @ViewChild('visualisationContainer')
  public visualisationContainer: ElementRef<HTMLElement>;
  public loadingSpaceFactory: LoadingSpaceFactory = new LoadingSpaceFactory();
  private scene: Scene;
  private camera: PerspectiveCamera | OrthographicCamera;
  private renderer: WebGLRenderer;
  private controls: OrbitControls;
  private cameraFOV = 75;
  private mousePosition = new Vector2();
  private renderingContext: RenderingContext<PerspectiveCamera | OrthographicCamera>;
  private canvasElement: HTMLElement;
  private raycaster: Raycaster;
  private plane: Plane;
  private intersectionWithPlane: Vector3;
  private isMouseEvents = !this.isOrto;
  private animationId: number;
  private loadingSpaceTypeMod: LoadingSpaceType;
  private projectMod: Project;

  constructor(private zone: NgZone) {}

  ngAfterViewInit(): void {
    const isMM = this.project?.options?.unit === 'mm';
    if (isMM) {
      this.loadingSpaceTypeMod = {
        ...convertLoadingSpacesDimensions([{ ...this.loadingSpaceType }], false)[0],
      };
    } else {
      this.loadingSpaceTypeMod = { ...this.loadingSpaceType };
    }
    this.projectMod = { ...this.project };
    if (!this.isOrto) {
      this.initializePreview();
      this.addObjects();
    } else {
      this.initializeOrto();
      this.addObjects();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.project && changes.project.currentValue) {
      this.projectMod = { ...changes.project.currentValue };
    }
    if (changes.loadingSpaceType && changes.loadingSpaceType.currentValue) {
      if (this.projectMod?.options?.unit === 'mm') {
        this.loadingSpaceTypeMod = convertLoadingSpacesDimensions(
          [{ ...changes.loadingSpaceType.currentValue }],
          false,
        )[0];
      } else {
        this.loadingSpaceTypeMod = { ...changes.loadingSpaceType.currentValue };
      }
    }

    if (changes.loadingSpaceType && changes.loadingSpaceType.currentValue) {
      if (this.isOrto && !this.loadingSpaceTypeMod.length1) {
        this.scene = new Scene();
      }
      this.loadingSpaceTypeMod = this.addViewToLoadingSpaceType(this.loadingSpaceTypeMod);
      this.addObjects();
    }
  }

  ngOnDestroy(): void {
    this.renderer.dispose();
    this.renderer.forceContextLoss();
    this.renderingContext = null;
    if (!this.isMouseEvents) {
      this.deactivateMouseEvents();
    }
  }

  private initMouseEvents(): void {
    if (!this.renderingContext?.renderer) {
      return;
    }
    this.canvasElement = this.renderingContext.renderer.domElement;
    this.raycaster = new Raycaster();
    this.mousePosition.set(0, 0);
    this.plane = new Plane();
    this.intersectionWithPlane = new Vector3();
    this.activateMouseEvents();
  }

  private addViewToLoadingSpaceType(loadingSpaceType: LoadingSpaceType): LoadingSpaceType {
    return {
      ...loadingSpaceType,
      view: this.view,
    };
  }

  public activateMouseEvents(): void {
    // this.canvasElement.addEventListener('pointermove', this.onMouseMove.bind(this), false);
    this.canvasElement.addEventListener('pointerdown', this.onMouseDown.bind(this), false);
  }

  public deactivateMouseEvents(): void {
    // this.canvasElement.removeEventListener('pointermove', this.onMouseMove, false);
    this.canvasElement.removeEventListener('pointerdown', this.onMouseDown, false);
  }

  private detectIntersections(event: MouseEvent): Intersection[] {
    const rect: ClientRect = this.canvasElement.getBoundingClientRect();
    this.mousePosition.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mousePosition.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mousePosition, this.renderingContext.camera);
    this.raycaster.ray.intersectPlane(this.plane, this.intersectionWithPlane);
    const intersects = this.raycaster.intersectObjects(this.scene.children, true);
    return intersects;
  }

  private onMouseDown(event: MouseEvent): void {
    event.preventDefault();
    const intersections = this.detectIntersections(event).map((intersect) => intersect.object.name);
    this.clicked.emit(intersections);
  }

  // If needed for hover effects otherwise delete
  // private onMouseMove = (event: MouseEvent): void => {
  //   event.preventDefault();
  //   const intersections = this.detectIntersections(event).map((intersect) => intersect.object.name);
  // };

  private calculateLengthOfCabin(): number {
    const unit =
      !!this.projectMod && !!this.projectMod.options?.unit ? this.projectMod.options?.unit : 'cm';
    const scalar = this.isOrto ? 100 : unit === 'lb' ? 2.54 : 100;
    const divideScalar = unit === 'lb' ? 2.54 : 1;
    const isBus = !this.loadingSpaceTypeMod.limit
      ? this.loadingSpaceTypeMod.length * scalar <= 48000 / divideScalar
      : this.loadingSpaceTypeMod.limit <= 1200;
    const isSolotSet = !this.loadingSpaceTypeMod.limit
      ? this.loadingSpaceTypeMod.length * scalar >= 48100 / divideScalar &&
        this.loadingSpaceTypeMod.length * scalar <= 80000 / divideScalar
      : this.loadingSpaceTypeMod.limit >= 1201 &&
        this.loadingSpaceTypeMod.limit <= 21000 / divideScalar;
    const isStandard = !this.loadingSpaceTypeMod.limit
      ? this.loadingSpaceTypeMod.length * scalar > 80000 / divideScalar
      : this.loadingSpaceTypeMod.limit * scalar > 21000 / divideScalar;
    if (isSolotSet) {
      return unit === 'cm' || unit === 'mm' ? 15000 : 15000 / divideScalar;
    }
    if (isBus) {
      return unit === 'cm' || unit === 'mm' ? 17000 : 17000 / divideScalar;
    }
    if (isStandard) {
      return unit === 'cm' || unit === 'mm' ? 22000 + 3000 : (22000 + 3000) / divideScalar;
    }
    return 0;
  }

  private animate = () => {
    this.renderer.render(this.scene, this.camera);
    this.controls.update();
    this.animationId = requestAnimationFrame(this.animate);
  };

  private calculateCameraPosition(objectSize: number): number {
    const fovRadians = this.cameraFOV * (Math.PI / 180);
    const cabinLength = this.calculateLengthOfCabin();
    const unit =
      !!this.projectMod && !!this.projectMod.options?.unit ? this.projectMod.options?.unit : 'cm';

    let additionalLength = -9000;
    if (this.loadingSpaceTypeMod.type === 1) {
      additionalLength = cabinLength;
    }
    if (this.loadingSpaceTypeMod.type === 3) {
      additionalLength = 5000;
    }
    if (this.loadingSpaceTypeMod.type === 4) {
      additionalLength = 5000;
    }

    let correction = 0;
    if (this.loadingSpaceTypeMod.type === 1) {
      correction = unit === 'cm' || unit === 'mm' ? -2500 : 0;
    }
    if (this.loadingSpaceTypeMod.type === 2) {
      correction = unit === 'cm' || unit === 'mm' ? 42500 : 0;
    }

    return Math.abs((objectSize + additionalLength) / Math.sin(fovRadians / 2)) + correction;
  }

  private initializeOrto(): void {
    if (!this.visualisationContainer) {
      return;
    } else {
      this.zone.runOutsideAngular(() => {
        this.scene = new Scene();
        this.camera = new OrthographicCamera(
          this.width / -2,
          this.width / 2,
          this.height / 2,
          this.height / -2,
          -100000000,
          100000000,
        );
        this.renderer = new WebGLRenderer({
          antialias: true,
          alpha: true,
          preserveDrawingBuffer: true,
        });
        this.renderer.outputColorSpace = LinearSRGBColorSpace;
        this.renderer.setSize(this.width, this.height);
        this.visualisationContainer.nativeElement.appendChild(this.renderer.domElement);
        this.renderingContext = new RenderingContext(this.scene, this.renderer, this.camera);
        this.initializeControls();
        this.animate();
      });
    }
  }

  private initializePreview(): void {
    this.zone.runOutsideAngular(() => {
      this.scene = new Scene();
      this.camera = new PerspectiveCamera(
        this.cameraFOV,
        this.width / this.height,
        100,
        1000000000000,
      );
      this.renderer = new WebGLRenderer({
        antialias: true,
        alpha: true,
        preserveDrawingBuffer: true,
      });
      this.renderer.outputColorSpace = LinearSRGBColorSpace;
      this.renderer.setSize(this.width, this.height);
      if (this.visualisationContainer) {
        this.visualisationContainer.nativeElement.appendChild(this.renderer.domElement);
      }
      this.renderingContext = new RenderingContext(this.scene, this.renderer, this.camera);
      this.initializeControls();
      // this.animate();
    });
  }

  private addObjects(): void {
    if (this.loadingSpaceTypeMod === undefined) {
      return;
    }
    this.scene = new Scene();
    if (this.renderingContext) {
      this.renderingContext.scene = this.scene;
      this.renderingContext.camera = this.camera;
    }
    if (this.isOrto && (this.view === 'left' || this.view === 'setLeft')) {
      this.scene.translateX(-10000000);
    }
    const unit =
      !!this.projectMod && !!this.projectMod.options?.unit ? this.projectMod.options?.unit : 'cm';
    const loadingSpaces = this.loadingSpaceFactory.createLoadingSpace(
      this.loadingSpaceTypeMod,
      unit,
    );
    const scalar = unit === 'lb' ? 2.54 : 100;
    if (this.renderingContext) {
      loadingSpaces.draw(this.renderingContext);
    }
    const totalWidth = (this.loadingSpaceTypeMod.width * scalar) / 2;
    const totalHeight = (this.loadingSpaceTypeMod.height * scalar) / 2;
    const setHeight = this.loadingSpaceTypeMod.set
      ? (this.loadingSpaceTypeMod.height1 * scalar) / 2
      : 0;
    const setLength = this.loadingSpaceTypeMod.set
      ? this.loadingSpaceTypeMod.length1 * scalar + 3000
      : 0;
    const totalLength = (this.loadingSpaceTypeMod.length * scalar + setLength) / 2;
    const cabinOffset = this.loadingSpaceTypeMod.type === 1 ? this.calculateLengthOfCabin() / 2 : 0;
    const rotation = Math.PI;
    this.scene.rotation.x = rotation;
    this.scene.rotation.z = rotation;
    if (this.isOrto && (this.view === 'left' || this.view === 'setLeft')) {
      this.scene.rotation.x = (180 * Math.PI) / 180;
    }
    if (this.isOrto && (this.view === 'back' || this.view === 'setBack')) {
      this.scene.rotation.y = (90 * Math.PI) / 180;
    }
    const height = totalHeight > setHeight ? totalHeight : setHeight;
    let maxSize = height > totalLength ? height : totalLength;
    if (unit === 'cm' || unit === 'mm') {
      this.scene.translateZ(-(totalLength - cabinOffset));
      this.scene.translateX(-totalWidth);
      this.scene.translateY(-height);
    } else {
      const totalLength =
        (this.loadingSpaceTypeMod.length * 100) / 2 -
        cabinOffset +
        (this.loadingSpaceTypeMod.set ? (this.loadingSpaceTypeMod.length1 * 100) / 2 : 0);
      this.scene.translateZ(-totalLength);
      this.scene.translateX((-this.loadingSpaceTypeMod.width * 100) / 2);
      this.scene.translateY((-this.loadingSpaceTypeMod.height * 100) / 2);
      maxSize = this.loadingSpaceTypeMod.type === 2 ? totalLength + 12000 : totalLength;
    }

    if (!this.isOrto && this.camera && this.camera.position) {
      const distance = this.calculateCameraPosition(maxSize) - 4000;
      this.camera.position.set(distance, distance * 0.3, distance * 0.7);
      this.camera.updateProjectionMatrix();
      this.camera.updateMatrix();
    } else if (this.isOrto && this.camera && this.camera.position) {
      const cabinOffset = this.loadingSpaceTypeMod.type === 1 ? this.calculateLengthOfCabin() : 0;
      let length = this.loadingSpaceTypeMod.set
        ? this.loadingSpaceTypeMod.length + this.loadingSpaceTypeMod.length1 + cabinOffset
        : this.loadingSpaceTypeMod.length;
      const height2 =
        unit === 'cm' ? this.loadingSpaceTypeMod.height : this.loadingSpaceTypeMod.height * 100;
      const totalLength =
        this.loadingSpaceTypeMod.length / 2 +
        cabinOffset +
        (this.loadingSpaceTypeMod.set ? this.loadingSpaceTypeMod.length1 / 2 : 0);
      length = unit === 'cm' ? length : totalLength;
      if (this.view === 'left' || this.view === 'setLeft') {
        const zoom = unit === 'cm' ? (this.loadingSpaceTypeMod.type === 2 ? 0.007 : 0.09) : 0.17;
        this.camera.zoom = Math.min(this.width / length, this.height / height2) * zoom;
      }
      if (this.view === 'back' || this.view === 'setBack') {
        const zoom = unit === 'cm' ? (this.loadingSpaceTypeMod.type === 2 ? 0.007 : 0.09) : 0.8;
        this.camera.zoom = Math.min(this.width / length, this.height / height2) * zoom;
      }
      this.camera.position.set(100, 0, 0);
      this.camera.updateProjectionMatrix();
      this.camera.updateMatrix();
    }
    if (this.isOrto && (this.view === 'left' || this.view === 'setLeft')) {
      this.scene.translateX(10000000);
    }
    if (this.isMouseEvents) {
      this.initMouseEvents();
    }
    if (this.isOrto) {
      setTimeout(() => cancelAnimationFrame(this.animationId), 0);
    }
    this.renderer?.render(this.scene, this.camera);
    this.controls?.update();
  }

  private initializeControls(): void {
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = false;
    this.controls.screenSpacePanning = false;
    this.controls.minDistance = 10;
    this.controls.maxDistance = 400000;
    this.controls.maxPolarAngle = Math.PI;
    if (this.isOrto) {
      this.controls.enablePan = false;
      this.controls.enableRotate = false;
    }
  }
}
